/**
 * Single source of truth for the app's brand surface (name + logo).
 *
 *   - org tenant  -> the organization's name + logo (resolved pre-auth from the
 *                    public endpoint, so it's available on the login screen too)
 *   - admin / no org -> the generic fallback brand ("Aristotle")
 *
 * Replaces the hardcoded "Aristotle" strings scattered through the UI.
 */
import { useContext } from 'react'

import { TenantContext } from './TenantProvider'

export interface Brand {
  name: string
  logoUrl: string | null
  /**
   * Prominent brand headline shown in the top banner and the home page's
   * "Welcome to …". Usually identical to `name`, but client-branded vanity
   * deployments override it (see VANITY_BRAND_NAMES) while the rest of the UI
   * keeps the plain org name.
   */
  headline: string
  /**
   * True when this org is a client-branded vanity deployment whose `headline`
   * replaces the platform (ITG) logo in the top banner's left slot.
   */
  vanity: boolean
  /** CDN filename of the login-screen hero image for this org. */
  loginImage: string
  /**
   * Brand name shown above the sign-in form on the login screen. Usually the
   * plain org name, but client-branded deployments override it (see
   * LOGIN_NAME_OVERRIDES).
   */
  loginName: string
}

/** Default login-screen hero image (used by every org without an override). */
export const DEFAULT_LOGIN_IMAGE = 'Aristotle-1.png'

/**
 * Per-org login-screen hero image overrides, keyed by org slug. Everyone else
 * uses DEFAULT_LOGIN_IMAGE.
 */
export const LOGIN_IMAGE_OVERRIDES: Record<string, string> = {
  aristotle: 'Aristotle-2.png',
}

/**
 * Per-org overrides for the brand name shown on the login screen (above the
 * sign-in form), keyed by org slug. Everyone else shows the plain org name.
 */
export const LOGIN_NAME_OVERRIDES: Record<string, string> = {
  aristotle: 'Aristotle AI',
}

/**
 * Per-org overrides for the prominent brand headline (top banner + home page
 * "Welcome to …"), keyed by org slug. Mirrors the vanity-domain concept in
 * tenant.ts (VANITY_ORG_DOMAINS). Everything else in the UI keeps the plain org
 * name, per the client's request to only rebrand those two spots.
 */
export const VANITY_BRAND_NAMES: Record<string, string> = {
  aristotle: 'Aristotle.ai',
}

/** Fallback brand whenever no org is resolved (admin portal, apex, crashes). */
export const FALLBACK_BRAND: Brand = {
  name: 'Aristotle',
  logoUrl: null,
  headline: 'Aristotle',
  vanity: false,
  loginImage: DEFAULT_LOGIN_IMAGE,
  loginName: 'Aristotle',
}

export default function useBrand(): Brand {
  const tenant = useContext(TenantContext)
  if (tenant?.kind === 'org') {
    const { name, slug, logoUrl } = tenant.org
    const override = VANITY_BRAND_NAMES[slug]
    return {
      name,
      logoUrl,
      headline: override ?? name,
      vanity: override != null,
      loginImage: LOGIN_IMAGE_OVERRIDES[slug] ?? DEFAULT_LOGIN_IMAGE,
      loginName: LOGIN_NAME_OVERRIDES[slug] ?? name,
    }
  }
  return FALLBACK_BRAND
}
